import React, { useEffect, useMemo, useState } from 'react';
import { getCoreRowModel } from '@tanstack/react-table';

// Theme
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { makeStyles } from '@mui/styles';

// Utils
import { makeNearReaderColumn } from '../../../utils/columns/makeColumn';
import { getNearReaders } from '../../../services/reader.service';

// Redux
import { useAppSelector } from '../../../redux/hook';

// Types
import { Reader } from '../../../types/Reader';
import { GetNearReaderRequest } from '../../../requestModels/getReaderRequest';

// Components
import { useNewTable, Table } from '../../common/table/index';
import { FilterableTypes } from '../../../types/FilterableTypes';

interface NearReaderProp {
  reader: Reader;
  accessKey?: string;
  shouldFetch: boolean;
  hoveredImei?: string | null;
  onNearReaderHover?: (imei: string | null) => void;
  // Emit active filters up to parent so Map can sync
  onFiltersChange?: (opsStatus: string | null, intendedUse: string | null) => void;
}

const useStyles = makeStyles(() => ({
  toggleButtonGroup: {
    height: '30px',
    padding: 2,
    backgroundColor: 'white',
  },
  toggleButton: {
    '&.MuiToggleButton-root': {
      fontSize: 'xx-small',
      whiteSpace: 'nowrap',
    },
  },
}));

export default function NearReaders(props: NearReaderProp) {
  const classes = useStyles();
  const [nearParamFilter, setNearParamFilter] = useState<GetNearReaderRequest | null>(null);
  const settingState = useAppSelector((state) => state.settings);

  // Default ops status is 'all', intended use is 'all'
  const [toggleReaderOpsStatus, setToggleReaderOpsStatus] = useState<string | null>('all');
  const [toggleIntendedUse, setToggleIntendedUse] = useState<string | null>('all');

  const handleScopeReader = (_: React.MouseEvent<HTMLElement>, tabName: string | null) => {
    if (tabName !== null) setToggleReaderOpsStatus(tabName);
  };

  const handleIntendedUseButton = (_: React.MouseEvent<HTMLElement>, tabName: string | null) => {
    setToggleIntendedUse(tabName ?? 'all');
  };

  // Emit filter changes up to parent (ReaderDetail) so Map can sync
  useEffect(() => {
    props.onFiltersChange?.(toggleReaderOpsStatus, toggleIntendedUse);
  }, [toggleReaderOpsStatus, toggleIntendedUse]);

  const filterParams = useMemo(() => {
    const { location, lockedLocation } = props.reader;

    const params: GetNearReaderRequest = {
      lat: lockedLocation?.latitude ?? location?.latitude ?? 0,
      lng: lockedLocation?.longitude ?? location?.longitude ?? 0,
      distanceInMeters: 10000,
      filters: [],
    };

    if (props.reader.stationIataCode) {
      params.filters.push({
        stationIataCode: props.reader.stationIataCode,
        filterType: FilterableTypes.Equals,
      });
    }

    // Intended use filter — only Stationary & Mobile
    if (toggleIntendedUse === 'stationary') {
      params.filters.push({
        isMobile: '0',
        filterType: FilterableTypes.Equals,
      });
    } else if (toggleIntendedUse === 'mobile') {
      params.filters.push({
        isMobile: '1',
        filterType: FilterableTypes.Equals,
      });
    }

    // Ops status filter
    if (toggleReaderOpsStatus === 'reporting') {
      params.filters.push({ opsStatus: '0', filterType: FilterableTypes.Equals });
    } else if (toggleReaderOpsStatus === 'not-reporting') {
      params.filters.push({ opsStatus: '2', filterType: FilterableTypes.Equals });
    } else if (toggleReaderOpsStatus === 'blacklisted') {
      params.filters.push({ opsStatus: '3', filterType: FilterableTypes.Equals });
    }
    // 'all' → no opsStatus filter added

    return params;
  }, [props.reader, toggleReaderOpsStatus, toggleIntendedUse]);

  useEffect(() => {
    setNearParamFilter((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(filterParams)) return filterParams;
      return prev;
    });
  }, [filterParams]);

  const columns = useMemo(() => makeNearReaderColumn(settingState), [settingState]);

  const table = useNewTable<Reader>({
    columns,
    fetchAsync: getNearReaders as any,
    params: nearParamFilter ?? undefined,
    serverSide: true,
    shouldFetch: props.shouldFetch && nearParamFilter !== null,
    initialState: {
      pagination: { pageSize: 20, pageIndex: 0 },
    },
  });

  useEffect(() => {
    if (table.data.length > 0) {
      // dispatch(updateNearReaderState(table.data)); // turned off per Alex
    }
  }, [table.data]);

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        {/* Intended Use filter — Stationary & Mobile only */}
        <ToggleButtonGroup
          value={toggleIntendedUse}
          exclusive
          onChange={handleIntendedUseButton}
          aria-label='Intended use filter'
          className={classes.toggleButtonGroup}
          style={{ marginLeft: 5 }}
        >
          <ToggleButton value='all' className={classes.toggleButton}>
            All
          </ToggleButton>
          <ToggleButton value='stationary' className={classes.toggleButton}>
            Stationary
          </ToggleButton>
          <ToggleButton value='mobile' className={classes.toggleButton}>
            Mobile
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Ops Status filter — below intended use */}
        <ToggleButtonGroup
          value={toggleReaderOpsStatus}
          exclusive
          onChange={handleScopeReader}
          aria-label='Ops status filter'
          className={classes.toggleButtonGroup}
          style={{ marginLeft: 5 }}
        >
          <ToggleButton value='all' className={classes.toggleButton}>
            All
          </ToggleButton>
          <ToggleButton
            value='reporting'
            className={classes.toggleButton}
            sx={{ '&.MuiToggleButton-root': { color: '#2e7d32' }, '&.Mui-selected': { color: '#fff' } }}
          >
            Reporting
          </ToggleButton>
          <ToggleButton
            value='not-reporting'
            className={classes.toggleButton}
            sx={{ '&.MuiToggleButton-root': { color: '#c62828' }, '&.Mui-selected': { color: '#fff' } }}
          >
            Not Reporting
          </ToggleButton>
          <ToggleButton
            value='blacklisted'
            className={classes.toggleButton}
            sx={{ '&.MuiToggleButton-root': { color: '#333' }, '&.Mui-selected': { color: '#fff' } }}
          >
            Blacklisted
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      <Table
        {...table}
        showSearchBar={false}
        showColumnToggler={false}
        showAction={false}
        showSpinner={false}
        getCoreRowModel={getCoreRowModel()}
        rowProps={(row: any) => {
          const imei = row.original?.imei;
          const isHighlighted = props.hoveredImei === imei;
          return {
            onMouseEnter: () => props.onNearReaderHover?.(imei),
            onMouseLeave: () => props.onNearReaderHover?.(null),
            style: {
              cursor: 'pointer',
              transition: 'background 0.15s ease',
              background: isHighlighted ? '#FFE066' : undefined,
              boxShadow: isHighlighted ? 'inset 4px 0 0 #FFD600' : 'inset 4px 0 0 transparent',
              fontWeight: isHighlighted ? 600 : undefined,
            },
          };
        }}
      />
    </>
  );
}
